/**
 * The sentence shown for each code the join routes can refuse with.
 *
 * A table of its own, for the same reason `signinErrors.ts` is one: the Go
 * guard beside the handlers (TestEveryJoinErrorCodeHasSPACopy) reads the
 * constants out of the source and compares them with this file, in both
 * directions. Without it, a refusal added on one side of the wire shows the
 * person `handle()`'s fallback — "Request failed (409)" — which tells them
 * nothing about what to do.
 *
 * Neither sentence repeats an address. The person reading them proved both
 * addresses a moment ago, so nothing would be leaked, but the rule is worth
 * keeping uniform: a message that echoes its input is one that can be made to
 * say things.
 */
export const JOIN_ERRORS: Record<string, string> = {
  nothing_to_join:
    'There is no second account to join. Sign in with it again to start over.',
  // A join needs both sign-ins to be under ten minutes old (B14). The old
  // remedy, to sign in with the other account again, no longer works: by then
  // the account this browser holds is the stale one, so that sign-in replaces
  // it instead of offering a join. Both, within ten minutes, is what works.
  join_expired:
    'That took too long, or the other sign-in is no longer valid. '
    + 'Sign in again with both accounts, one after the other, within ten minutes.',
}

/**
 * The sentence for what went wrong, or one that says what to do when there is
 * no sentence for it. A written sentence exists for everything this route
 * refuses on purpose.
 *
 * A 5xx is not a refusal: the server decided nothing and changed nothing
 * (RequireMember's 503 session_unavailable while the database is away, the
 * join's own 500 "Nothing was changed. Try again in a moment."), so the
 * question stands and Yes can be pressed again. The fallback told the person
 * to sign in with the other account again, which they need not do, so a 5xx
 * shows the server's sentence, which says to try again, or JOIN_TRY_AGAIN
 * when the answer carried none (review 6, app request 3; the app does the
 * same since 30140cb). Pass the status and the server's sentence for that;
 * a caller that passes only the code gets the table, as before.
 */
export function joinErrorMessage(code: string, status?: number, serverSentence?: string): string {
  // hasOwn, not a bare lookup, as in signinErrors.ts: `JOIN_ERRORS[code]`
  // reaches Object.prototype, so a code such as "toString" answered a function
  // and "__proto__" an object, and the page rendered neither as a sentence.
  if (Object.hasOwn(JOIN_ERRORS, code)) return JOIN_ERRORS[code]
  if (status !== undefined && status >= 500 && status <= 599) {
    // 'unknown' is handle()'s code for a body that was not the server's JSON,
    // whose words are "Request failed (502)" and say nothing.
    const said = (serverSentence ?? '').trim()
    return code !== 'unknown' && said !== '' ? said : JOIN_TRY_AGAIN
  }
  return JOIN_FALLBACK
}

/** What a 5xx with no sentence of its own shows: nothing was decided, so try again. */
export const JOIN_TRY_AGAIN = 'That could not be finished just now. Try again in a moment.'

/** What is shown for a refusal with no sentence of its own. */
export const JOIN_FALLBACK =
  'That could not be finished. Sign in with the other account again to try once more.'
